/**
 * The P6 staff (manager) wire contract, defined once.
 *
 * This is deliberately **not** the control-channel Envelope. A staff request is
 * a plain authenticated HTTP call whose authority is the staff token:
 *
 *   - the **tenant** comes from the signed claim, never from the body;
 *   - the **account** is selected per request and authorised server-side against
 *     that tenant (the scope decision — docs/13 §2);
 *   - the **role** comes from the claim, and the server checks it before doing
 *     anything, never from a field the caller can set.
 *
 * Keeping these shapes here (rather than hand-writing them in the service and the
 * app) is the same rule P5 applied to the preview: one definition, imported by
 * both sides.
 */

import { z } from 'zod';

export const STAFF_ROLE_WIRE = ['ADMIN', 'DEALER', 'SUPERVISOR', 'READ_ONLY'] as const;
export type StaffRoleWire = (typeof STAFF_ROLE_WIRE)[number];

export const StaffRoleWire = z.enum(STAFF_ROLE_WIRE);

/** What a role may do. Derived server-side from the role; echoed to the UI. */
export interface StaffPermissions {
  readonly read: true;
  /** Confirm/reject a `REQUEST` order. */
  readonly deal: boolean;
  /** Deposit, withdrawal, correction. */
  readonly balance: boolean;
  /** Credit (a goodwill balance increase) — supervisor/admin only. */
  readonly credit: boolean;
  /**
   * Correct a booked deal (P12.3). Supervisor/admin only: a correction restates
   * money that was already realised rather than executing at the desk's price,
   * so it is the one trade-editing action a dealer may not perform.
   */
  readonly correct: boolean;
  /** Account state controls (read-only / disable). */
  readonly accountState: boolean;
  /**
   * Create a deposit/withdrawal request and cancel one's own (P12.6). A dealer may
   * ask; asking is not deciding.
   */
  readonly paymentRequest: boolean;
  /**
   * Approve or reject a deposit/withdrawal request (P12.6). Supervisor/admin only:
   * approval is what moves the money. The requester can never decide their own
   * request, whatever their role.
   */
  readonly paymentApprove: boolean;
  /** Read the admin console's configuration (P7). */
  readonly configRead: boolean;
  /** Write groups, symbols and session calendars (P7). */
  readonly configWrite: boolean;
  /** Create/modify staff accounts, subject to the ADMIN rules (P7). */
  readonly staffAdmin: boolean;
  /** Tenant branding and settings — ADMIN only (P7). */
  readonly tenantSettings: boolean;
}

export interface StaffProfile {
  readonly staff_id: string;
  readonly login: string;
  readonly display_name: string;
  readonly role: StaffRoleWire;
}

export const StaffLoginRequest = z.strictObject({
  login: z.string().min(3).max(32),
  password: z.string().min(8).max(256),
  /** Six-digit TOTP. Required: there is no password-only staff login. */
  code: z.string().regex(/^\d{6}$/),
});

/**
 * The licence-derived part of a staff session (P9).
 *
 * `features` is what the console may render; the server refuses the same
 * features independently. The entitlement state and banner are here so the desk
 * can show *why* something is unavailable instead of an unexplained 403.
 */
export interface StaffEntitlements {
  readonly entitlements_version: number;
  readonly features: readonly string[];
  readonly license_state: string;
  readonly can_create_accounts: boolean;
  readonly can_create_tenants: boolean;
  readonly entitlement_banner: string | null;
  readonly limits: Readonly<Record<string, number>>;
}

export interface StaffLoginResponse extends StaffEntitlements {
  readonly token_type: 'Bearer';
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly refresh_expires_in: number;
  readonly tenant_id: string;
  readonly tenant_slug: string;
  readonly staff: StaffProfile;
  readonly permissions: StaffPermissions;
  readonly server_time: string;
}

export const StaffRefreshRequest = z.strictObject({
  refresh_token: z.string().min(10),
});

export interface StaffMeResponse extends StaffEntitlements {
  readonly staff: StaffProfile;
  readonly permissions: StaffPermissions;
  readonly tenant_id: string;
  readonly tenant_slug: string;
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* Accounts                                                            */
/* ------------------------------------------------------------------ */

export interface StaffAccountRow {
  readonly account_id: string;
  readonly login: string;
  readonly display_name: string | null;
  readonly currency: string;
  readonly digits: number;
  readonly state: 'ACTIVE' | 'READ_ONLY' | 'DISABLED' | 'ARCHIVED';
  readonly balance_minor: string;
  readonly group_id: string;
  readonly group_name: string;
  readonly open_positions: number;
  readonly working_orders: number;
  readonly dealer_pending: number;
  readonly last_login_at: string | null;
}

export interface StaffAccountListResponse {
  readonly accounts: readonly StaffAccountRow[];
  readonly total: number;
  readonly server_time: string;
}

/** One open position, with the server's own floating P&L and the mark it used. */
export interface StaffPosition {
  readonly position_id: string;
  readonly symbol_id: string;
  readonly symbol: string;
  readonly side: 'BUY' | 'SELL';
  readonly volume_e4: string;
  readonly open_price_e8: string;
  readonly sl_e8: string | null;
  readonly tp_e8: string | null;
  readonly opened_at: string;
  readonly floating_minor: string;
  readonly mark_e8: string;
}

export interface StaffWorkingOrder {
  readonly order_id: string;
  readonly symbol_id: string;
  readonly symbol: string;
  readonly side: 'BUY' | 'SELL';
  readonly order_type: string;
  readonly tif: string;
  readonly state: string;
  readonly volume_e4: string;
  readonly filled_volume_e4: string;
  readonly limit_price_e8: string | null;
  readonly stop_price_e8: string | null;
  readonly requested_price_e8: string | null;
  readonly dealer_pending: boolean;
  readonly created_at: string;
}

/** The mark the server valued this account at (one snapshot, one clock). */
export interface StaffMark {
  readonly symbol: string;
  readonly bid_e8: string;
  readonly ask_e8: string;
  readonly at_ms: string;
  readonly age_ms: string;
  readonly stale: boolean;
}

export interface StaffAccountDetailResponse {
  readonly account: StaffAccountRow;
  /** The P5 account snapshot: the same one-mark valuation the terminal shows. */
  readonly snapshot: import('./preview.js').AccountSnapshot;
  readonly positions: readonly StaffPosition[];
  readonly orders: readonly StaffWorkingOrder[];
  /**
   * P12.3: the booked-deal history, newest last, with each correction's link and
   * the reason and operator that explain it. Read from the same engine state as
   * the positions, so the desk's "correct this fill" list cannot disagree with
   * the exposure above it.
   */
  readonly deals: readonly StaffDealRow[];
  readonly marks: readonly StaffMark[];
  readonly symbols: readonly import('./preview.js').PreviewSymbol[];
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* Dealing                                                             */
/* ------------------------------------------------------------------ */

export interface StaffQueueRow {
  readonly order_id: string;
  readonly account_id: string;
  readonly account_login: string;
  readonly symbol_id: string;
  readonly symbol: string;
  readonly side: 'BUY' | 'SELL';
  readonly order_type: string;
  readonly tif: string;
  readonly volume_e4: string;
  /** What the client asked for (its own expected price), if it sent one. */
  readonly requested_price_e8: string | null;
  readonly limit_price_e8: string | null;
  readonly created_at: string;
  readonly age_ms: string;
}

export interface StaffQueueResponse {
  readonly orders: readonly StaffQueueRow[];
  readonly server_time: string;
}

export const DealerDecisionRequest = z.strictObject({
  account_id: z.uuid(),
  order_id: z.uuid(),
  decision: z.enum(['ACCEPT', 'REJECT']),
  /** Required for a rejection: the client is told why. */
  reason: z.string().min(3).max(120).optional(),
});

export const RequoteRequest = z.strictObject({
  account_id: z.uuid(),
  order_id: z.uuid(),
  /** The price the desk is offering, e8. Must be positive. */
  price_e8: z.string().regex(/^\d{1,18}$/),
});

/**
 * A requote is a **communication**, not a ledger event.
 *
 * The engine has no requote command and adding one would let a requote mutate or
 * lose a client's order (docs/02 §3.7). So this records what the desk told the
 * client — who, when, at what price — in the audit trail, and nothing else. The
 * client re-sends at the new price; an abandoned request simply expires from the
 * queue.
 */
export interface RequoteResponse {
  readonly ok: true;
  readonly order_id: string;
  readonly account_id: string;
  readonly price_e8: string;
  readonly audit_id: string;
  readonly server_time: string;
}

/** The engine's own answer plus the journal rows the action produced. */
export interface DealerDecisionResponse {
  readonly ok: boolean;
  readonly order_id: string;
  readonly decision: 'ACCEPT' | 'REJECT';
  /** The command response kind the engine returned (`DEALER_ACCEPTED` / `REJECTED`). */
  readonly response_kind: string;
  readonly rejection_code: string | null;
  /**
   * Every journal event this action produced, in order. A `REJECTED
   * E_NO_MONEY` can coexist with `DEALER_ACCEPTED` (the dealer said yes, the
   * system refused) — the desk must show both, which is why they are here and
   * not inferred from the response.
   */
  readonly events: readonly StaffJournalEvent[];
  readonly audit_id: string;
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* Balance operations                                                  */
/* ------------------------------------------------------------------ */

export const BALANCE_OPERATION_KINDS = ['DEPOSIT', 'WITHDRAWAL', 'CREDIT', 'CORRECTION'] as const;
export type BalanceOperationKindWire = (typeof BALANCE_OPERATION_KINDS)[number];
export const BalanceOperationKindWire = z.enum(BALANCE_OPERATION_KINDS);

export const BalanceOperationRequest = z.strictObject({
  account_id: z.uuid(),
  kind: BalanceOperationKindWire,
  /**
   * Positive magnitude in minor units. Required for every kind, including
   * CORRECTION — the schema is strict and a correction that omitted it was refused
   * as `E_PROTOCOL`, which is how P11's soak found that this comment used to say
   * "except CORRECTION".
   */
  amount_minor: z.string().regex(/^\d{1,15}$/),
  /**
   * Signed amount, required (and only allowed) for CORRECTION. This is the delta
   * the engine applies; `amount_minor` stays the magnitude in the request (and the
   * response reports the signed delta as `amount_minor`).
   */
  correction_delta_minor: z
    .string()
    .regex(/^-?\d{1,15}$/)
    .optional(),
  /**
   * The earlier movement this correction reverses. Only valid on a CORRECTION,
   * and the correction must negate that movement exactly (docs/15 §6).
   */
  reverses_op_id: z.uuid().optional(),
  reason: z.string().min(3).max(280),
});

export interface BalanceOperationResponse {
  readonly ok: true;
  readonly operation_id: string;
  readonly account_id: string;
  readonly kind: BalanceOperationKindWire;
  readonly amount_minor: string;
  readonly balance_after_minor: string;
  /** Journal seq of the `BALANCE_ADJUSTED` event. */
  readonly seq: string;
  /** Set when this correction reverses an earlier movement. */
  readonly reverses_op_id: string | null;
  readonly audit_id: string;
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* P12.3 — dealer trade editing and corrections                        */
/* ------------------------------------------------------------------ */

/**
 * One price, e8, as a canonical decimal string.
 *
 * `\d{1,18}` refuses a sign, a decimal point and scientific notation: a dealer
 * typing "1.2345" is a UI problem, never a price the server accepts.
 */
const E8 = z.string().regex(/^\d{1,18}$/);
/** One volume, e4, as a canonical decimal string. */
const E4 = z.string().regex(/^\d{1,14}$/);
const REASON = z.string().min(3).max(280);

/**
 * Change SL and/or TP on an open position.
 *
 * `null` clears a level, `undefined` leaves it alone — the same distinction the
 * client protocol makes, because getting it backwards silently removes client
 * protection (docs/11 K-6).
 */
export const PositionModifyRequest = z.strictObject({
  account_id: z.uuid(),
  position_id: z.uuid(),
  sl_e8: E8.nullable().optional(),
  tp_e8: E8.nullable().optional(),
  reason: REASON,
});

/**
 * Close all or part of an open position on the client's behalf.
 *
 * Omitting `volume_e4` closes the whole position; a partial close must be a
 * multiple of the symbol's volume step and no larger than what is open — the
 * engine enforces both.
 */
export const PositionCloseRequest = z.strictObject({
  account_id: z.uuid(),
  position_id: z.uuid(),
  volume_e4: E4.optional(),
  reason: REASON,
});

/** Reprice, resize or re-level a working (non-terminal) order. */
export const OrderModifyRequest = z.strictObject({
  account_id: z.uuid(),
  order_id: z.uuid(),
  volume_e4: E4.optional(),
  limit_price_e8: E8.optional(),
  stop_price_e8: E8.optional(),
  sl_e8: E8.nullable().optional(),
  tp_e8: E8.nullable().optional(),
  /** A new expiry for a `SPECIFIED` order; must be in the future. */
  expires_at: z.iso.datetime().optional(),
  reason: REASON,
});

/** Cancel a working order. A terminal order is refused `E_ORDER_STATE`. */
export const OrderCancelRequest = z.strictObject({
  account_id: z.uuid(),
  order_id: z.uuid(),
  reason: REASON,
});

/**
 * Correct a booked deal — the disputed-fill workflow.
 *
 * `correction_id` is the **idempotency reference**. A retry with the same id and
 * the same payload is a no-op that returns the recorded pair; the same id with a
 * different price, reason or deal is refused `E_CORRECTION_CONFLICT`. The desk
 * generates it once per correction, not once per click.
 */
export const DealCorrectionRequest = z.strictObject({
  account_id: z.uuid(),
  deal_id: z.uuid(),
  corrected_price_e8: E8,
  reason: REASON,
  correction_id: z.uuid(),
});

/** The journal rows the action produced, in order. */
export type StaffTradeAction =
  | 'POSITION_MODIFY'
  | 'POSITION_CLOSE'
  | 'POSITION_PARTIAL_CLOSE'
  | 'ORDER_MODIFY'
  | 'ORDER_CANCEL'
  | 'DEAL_CORRECTION';

/**
 * The answer to every P12.3 staff action.
 *
 * One shape for five commands on purpose: the desk must render "the engine said
 * no" identically whether it was a stop the market had already passed, a
 * terminal order or a refused correction, and the events are always the truth
 * (`ok` is a summary of them, never a replacement).
 */
export interface StaffActionResponse {
  readonly ok: boolean;
  readonly action: StaffTradeAction;
  readonly account_id: string;
  /** The position, order or deal the operator named. */
  readonly subject_id: string;
  readonly response_kind: string | null;
  readonly rejection_code: string | null;
  readonly events: readonly StaffJournalEvent[];
  /** The account's realised balance before and after this action, minor units. */
  readonly balance_before_minor: string;
  readonly balance_after_minor: string;
  readonly audit_id: string;
  readonly server_time: string;
  /* Correction-only facts; null on the other four actions. */
  readonly correction_id: string | null;
  readonly idempotent: boolean;
  readonly corrected_price_e8: string | null;
  readonly original_deal_id: string | null;
  readonly reversal_deal_id: string | null;
  readonly rebook_deal_id: string | null;
}

/**
 * One booked deal, as the desk reads it.
 *
 * `correctable` is computed server-side from the engine's own rule (a deal that
 * booked money, is not itself a correction, and has not been corrected), so the
 * UI cannot offer a correction the engine will refuse.
 */
export interface StaffDealRow {
  readonly deal_id: string;
  readonly seq: string;
  readonly at: string;
  readonly deal_type: string;
  readonly symbol: string;
  readonly side: 'BUY' | 'SELL';
  readonly volume_e4: string;
  readonly price_e8: string;
  readonly requested_price_e8: string;
  readonly open_price_e8: string | null;
  readonly close_price_e8: string | null;
  readonly realised_pnl_minor: string;
  readonly balance_delta_minor: string;
  readonly order_id: string | null;
  readonly position_id: string | null;
  readonly corrects_deal_id: string | null;
  readonly correction_id: string | null;
  readonly correction_role: 'REVERSAL' | 'REBOOK' | null;
  readonly reason: string | null;
  readonly operator_login: string | null;
  /** The engine would accept a correction of this deal right now. */
  readonly correctable: boolean;
}

/** The staff detail's deal history: newest last, capped by the server. */
export interface StaffDealsResponse {
  readonly account_id: string;
  readonly deals: readonly StaffDealRow[];
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* Journal, sessions, audit, account state                             */
/* ------------------------------------------------------------------ */

export interface StaffJournalEvent {
  readonly seq: string;
  readonly type: string;
  readonly at: string;
  /** The engine payload, wire-encoded (bigints as canonical strings). */
  readonly data: unknown;
  /**
   * The staff action that caused this event, when one did. This is how a
   * `DEALER_ACCEPTED` event carries the dealer's identity without changing the
   * client-visible engine payload.
   */
  readonly staff: StaffEventAttribution | null;
}

export interface StaffEventAttribution {
  readonly audit_id: string;
  readonly staff_id: string;
  readonly staff_login: string;
  readonly staff_role: StaffRoleWire;
  readonly action: string;
}

export interface StaffAuditRow {
  readonly audit_id: string;
  readonly staff_id: string | null;
  readonly staff_login: string;
  readonly staff_role: StaffRoleWire;
  readonly action: string;
  readonly account_id: string | null;
  readonly order_id: string | null;
  readonly outcome: 'ALLOWED' | 'REFUSED' | 'FAILED';
  readonly code: string | null;
  readonly reason: string | null;
  readonly journal_seq: string | null;
  readonly created_at: string;
}

export interface StaffJournalResponse {
  readonly account_id: string;
  readonly events: readonly StaffJournalEvent[];
  readonly audit: readonly StaffAuditRow[];
  /** Highest seq in this page; pass it back as `after_seq` to continue. */
  readonly through_seq: string | null;
  readonly server_time: string;
}

export interface StaffSessionRow {
  readonly kind: 'TRADER' | 'STAFF';
  readonly login: string;
  readonly account_id: string | null;
  readonly session_id: string;
  readonly session_seq: string;
  readonly issued_at: string;
  readonly expires_at: string;
  readonly closed: boolean;
  readonly ip: string | null;
}

export interface StaffSessionsResponse {
  readonly sessions: readonly StaffSessionRow[];
  readonly server_time: string;
}

export const AccountStateRequest = z.strictObject({
  account_id: z.uuid(),
  state: z.enum(['ACTIVE', 'READ_ONLY', 'DISABLED']),
  reason: z.string().min(3).max(280),
});

export interface AccountStateResponse {
  readonly ok: true;
  readonly account_id: string;
  readonly state: 'ACTIVE' | 'READ_ONLY' | 'DISABLED';
  readonly previous_state: string;
  readonly audit_id: string;
  readonly server_time: string;
}

/** The error body every staff endpoint returns on refusal. */
export interface StaffErrorBody {
  readonly error: {
    readonly code: string;
    readonly message: string;
  };
}

/* ------------------------------------------------------------------ */
/* P7 admin configuration                                              */
/* ------------------------------------------------------------------ */

export type ConfigEntityKind = 'GROUP' | 'SYMBOL' | 'TENANT';
export type ConfigVersionStatus = 'SCHEDULED' | 'APPLIED' | 'CANCELLED' | 'SUPERSEDED';

/**
 * One configuration version. `before`/`after` are the full normalised payloads,
 * so the audit can always answer "what exactly changed" without joining history.
 */
export interface AdminConfigVersion {
  readonly version_id: string;
  readonly entity_kind: ConfigEntityKind;
  readonly entity_id: string | null;
  readonly action: string;
  readonly status: ConfigVersionStatus;
  readonly effective_from: string;
  readonly created_at: string;
  readonly applied_at: string | null;
  readonly operator_login: string;
  readonly reason: string;
  readonly impact: AdminImpactSummary;
}

export interface AdminImpactAccount {
  readonly account_id: string;
  readonly login: string;
  /** Account-currency minor-unit digits, so the console can render money. */
  readonly digits_ccy: number;
  readonly level_before_bp: string | null;
  readonly level_after_bp: string | null;
  readonly used_margin_before_minor: string;
  readonly used_margin_after_minor: string;
  readonly equity_minor: string;
  readonly margin_call: boolean;
  readonly stop_out: boolean;
}

export interface AdminImpactSummary {
  readonly accounts_total: number;
  readonly accounts_evaluated: number;
  /** Accounts that were fine before and would be in margin call/stop-out after. */
  readonly newly_margin_call: number;
  readonly newly_stop_out: number;
  readonly margin_call_before: number;
  readonly margin_call_after: number;
  readonly stop_out_before: number;
  readonly stop_out_after: number;
  readonly unvaluable: number;
  readonly truncated: boolean;
  readonly samples: readonly AdminImpactAccount[];
  readonly computed_at: string;
}

export interface AdminImpactRequest {
  readonly group_id: string;
  readonly proposed: unknown;
}

export interface AdminImpactResponse extends AdminImpactSummary {
  readonly group_id: string;
  /** True when the proposed config is the current live config (no change). */
  readonly unchanged: boolean;
}

export interface AdminSymbolRow {
  readonly id: string;
  readonly name: string;
  readonly base_ccy: string;
  readonly quote_ccy: string;
  readonly digits: number;
  readonly tick_size_e8: string;
  readonly contract_size: string;
  readonly volume_min_e4: string;
  readonly volume_max_e4: string;
  readonly volume_step_e4: string;
  readonly exec_mode: string;
  readonly fill_policy: string;
  readonly slippage_tolerance_e8: string;
  readonly stop_level_e8: string;
  readonly freeze_level_e8: string;
  /** P12.5 cross-source guard: maximum cross-feed mid deviation, e8. Null = off. */
  readonly cross_source_guard_e8: string | null;
  /** Cooldown during which entries are refused after a guard trip. */
  readonly cross_source_cooldown_ms: number | null;
  readonly enabled: boolean;
  readonly open_positions: number;
  readonly working_orders: number;
  readonly pending_config: AdminConfigVersion | null;
}

/**
 * One per-symbol economics row, as the console reads it (P12.1).
 *
 * `null` is **inherit** for every economics column, and it is what the console
 * must show as a placeholder rather than a value: a form that displays the
 * inherited number in the input cannot tell "this group sets 1:100" from "this
 * symbol inherits 1:100", and the first save turns one into the other.
 */
export interface AdminGroupSymbolRow {
  readonly symbol_id: string;
  readonly symbol: string;
  readonly trade_mode: string | null;
  readonly leverage: number | null;
  readonly margin_rate_bp: number | null;
  readonly swap_long_e8: string | null;
  readonly swap_short_e8: string | null;
  readonly swap_mode: string | null;
  readonly commission_mode: string | null;
  readonly commission_rate_minor: string | null;
  readonly commission_rate_bp: number | null;
  readonly spread_min_e8: string | null;
  readonly spread_max_e8: string | null;
  readonly markup_fixed_e8: string | null;
  readonly markup_bp: number | null;
  /** P12.5: the volume ladder, session windows and per-source rates, as stored. */
  readonly markup_tiers: readonly AdminMarkupTierRow[] | null;
  readonly markup_sessions: readonly AdminMarkupWindowRow[] | null;
  readonly markup_source_rates: Readonly<Record<string, { fixed_e8: string; bp: number }>> | null;
  readonly deviation_e8: string | null;
  readonly enabled: boolean;
}

/** One rung of a P12.5 markup ladder, as the console edits it. */
export interface AdminMarkupTierRow {
  readonly up_to_volume_e4: string | null;
  readonly fixed_e8: string;
  readonly bp: number;
}

/** One P12.5 markup session window in the tenant's server time. */
export interface AdminMarkupWindowRow {
  readonly weekdays: readonly number[];
  readonly from_minute: number;
  readonly to_minute: number;
}

export interface AdminGroupRow {
  readonly id: string;
  readonly name: string;
  readonly margin_mode: string;
  readonly currency: string;
  readonly leverage: number;
  readonly tier_table: readonly { up_to_minor: string | null; leverage: number }[] | null;
  readonly margin_call_level_bp: number;
  readonly stop_out_level_bp: number;
  readonly commission_mode: string;
  readonly commission_rate_minor: string;
  readonly commission_rate_bp: number;
  readonly swap_mode: string;
  readonly triple_weekday: number;
  readonly tier_basis: string;
  readonly hedged_factor_bp: number;
  readonly reserve_margin_for_pending: boolean;
  readonly swap_long_points_e8: string;
  readonly swap_short_points_e8: string;
  readonly swap_long_rate_bp: string;
  readonly swap_short_rate_bp: string;
  readonly allowed_symbols: readonly AdminGroupSymbolRow[];
  readonly account_count: number;
  readonly pending_config: AdminConfigVersion | null;
}

/**
 * One session slot (P12.2): a **quote** window and a **trade** window for one
 * weekday, in the symbol's timezone, as integer minutes since midnight
 * (`0..1440`, `from < to` always; a window that crosses midnight is two rows).
 */
export interface AdminSessionRow {
  readonly weekday: number;
  /** The window's position within its weekday (0..7). */
  readonly slot: number;
  readonly quote_from_min: number;
  readonly quote_to_min: number;
  readonly trade_from_min: number;
  readonly trade_to_min: number;
}

export interface AdminHolidayRow {
  readonly holiday_id: string;
  readonly symbol_id: string | null;
  readonly holiday_date: string;
  readonly description: string;
}

/**
 * The calendar's verdict for one symbol at the server's current instant,
 * computed by the trade path's resolver so the console cannot disagree with the
 * engine. `mode` is deliberately not part of it: `trade_mode` is per (group,
 * symbol), and averaging the groups into one cell would be a second chain.
 */
export interface AdminCalendarState {
  readonly quoting: boolean;
  readonly trading: boolean;
  /** One of `SESSION_REASONS` in `services/common/src/sessions.ts`. */
  readonly reason: string;
  /** One sentence: the operator's "why is it closed now". */
  readonly detail: string;
  readonly local: {
    readonly date: string;
    readonly weekday: number;
    readonly minute: number;
    readonly timezone: string;
  };
}

export interface AdminCalendarRow {
  readonly symbol_id: string;
  readonly symbol: string;
  /** `symbols.timezone`; `null` = inherit `tenant_timezone`. */
  readonly timezone: string | null;
  /** The tenant's zone, shown as the placeholder for an inherited symbol. */
  readonly tenant_timezone: string;
  readonly sessions: readonly AdminSessionRow[];
  readonly holidays: readonly AdminHolidayRow[];
  readonly effective: AdminCalendarState;
}

export interface AdminStaffRow {
  readonly staff_id: string;
  readonly login: string;
  readonly display_name: string;
  readonly role: StaffRoleWire;
  readonly state: 'ACTIVE' | 'DISABLED';
  readonly ip_allowlist: readonly string[];
  readonly created_at: string;
  readonly last_login_at: string | null;
  readonly active_sessions: number;
}

export interface AdminBrandingRow {
  readonly display_name: string;
  readonly logo_ref: string | null;
  readonly color_primary: string;
  readonly color_accent: string;
  readonly timezone: string;
  readonly default_currency: string;
  readonly slug: string;
  readonly name: string;
}

export interface AdminBootstrapResponse {
  readonly symbols: readonly AdminSymbolRow[];
  readonly groups: readonly AdminGroupRow[];
  readonly calendars: readonly AdminCalendarRow[];
  readonly staff: readonly AdminStaffRow[];
  readonly branding: AdminBrandingRow;
  readonly pending: readonly AdminConfigVersion[];
  readonly audit: readonly StaffAuditRow[];
  readonly server_time: string;
  readonly permissions: StaffPermissions;
}

export interface AdminSaveResponse {
  readonly ok: true;
  /** The group/symbol id the version targets. */
  readonly entity_id: string;
  readonly version_id: string;
  readonly status: ConfigVersionStatus;
  readonly effective_from: string;
  readonly impact: AdminImpactSummary | null;
  readonly server_time: string;
}

export interface AdminStaffCreateResponse {
  readonly ok: true;
  readonly staff_id: string;
  readonly login: string;
  readonly role: StaffRoleWire;
  /** The TOTP secret, returned exactly once at creation/reset. */
  readonly totp_secret: string;
  readonly otpauth_uri: string;
  readonly server_time: string;
}

export interface AdminMfaResetResponse {
  readonly ok: true;
  readonly staff_id: string;
  readonly login: string;
  readonly totp_secret: string;
  readonly otpauth_uri: string;
  readonly sessions_invalidated: true;
  readonly server_time: string;
}

export interface AdminStaffUpdateResponse {
  readonly ok: true;
  readonly staff_id: string;
  readonly role: StaffRoleWire;
  readonly state: 'ACTIVE' | 'DISABLED';
  readonly audit_id: string;
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* P12.6 — account lifecycle and money in/out                          */
/* ------------------------------------------------------------------ */

export const APPLICATION_ID_TYPES = [
  'PASSPORT',
  'NATIONAL_ID',
  'DRIVERS_LICENSE',
  'RESIDENCE_PERMIT',
] as const;
export type ApplicationIdType = (typeof APPLICATION_ID_TYPES)[number];

export const APPLICATION_DECISIONS = ['APPROVE', 'REJECT'] as const;
export type ApplicationDecision = (typeof APPLICATION_DECISIONS)[number];

export const PAYMENT_KINDS = ['DEPOSIT', 'WITHDRAWAL'] as const;
export type PaymentKindWire = (typeof PAYMENT_KINDS)[number];

export const PAYMENT_METHODS = ['BANK', 'CARD', 'CRYPTO', 'INTERNAL'] as const;
export type PaymentMethodWire = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type PaymentStatusWire = (typeof PAYMENT_STATUSES)[number];

/**
 * Submit an application to open an account.
 *
 * The applicant block is required for `INDIVIDUAL` (the console's only kind): the
 * wire schema refuses a half-filled KYC record before any service sees it. The
 * account's group, currency and opening deposit are the application's facts;
 * approval creates the account from exactly these.
 */
export const AccountApplicationCreateRequest = z.strictObject({
  first_name: z.string().min(1).max(120),
  last_name: z.string().min(1).max(120),
  /** ISO date. 18+ is enforced server-side; the schema only states the shape. */
  date_of_birth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  address_line1: z.string().min(1).max(200),
  address_line2: z.string().max(200).optional(),
  city: z.string().min(1).max(120),
  postal_code: z.string().max(32).optional(),
  country: z.string().regex(/^[A-Z]{2}$/),
  email: z.string().max(254).optional(),
  phone: z.string().max(32).optional(),
  id_type: z.enum(APPLICATION_ID_TYPES),
  id_number: z.string().min(3).max(64),
  document_ref: z.string().max(120).optional(),
  group_id: z.uuid(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Major units, e.g. "1000.00". */
  requested_deposit: z.string().regex(/^\d{1,12}(\.\d{1,4})?$/),
});

/**
 * Approve or reject one application.
 *
 * On approval the server allocates a login unless one is given, generates a
 * one-time password unless one is given, creates the account and posts the
 * requested opening deposit (`funding_seq` 1). The response carries the password
 * once; it is not stored anywhere.
 */
export const AccountApplicationDecisionRequest = z.strictObject({
  application_id: z.uuid(),
  decision: z.enum(APPLICATION_DECISIONS),
  reason: REASON,
  login: z.string().regex(/^\d{1,18}$/).optional(),
  password: z.string().min(8).max(256).optional(),
});

export interface StaffApplicationRow {
  readonly application_id: string;
  readonly kind: 'INDIVIDUAL' | 'SYSTEM';
  readonly applicant_name: string | null;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly date_of_birth: string | null;
  readonly address_line1: string | null;
  readonly address_line2: string | null;
  readonly city: string | null;
  readonly postal_code: string | null;
  readonly country: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly id_type: string | null;
  readonly id_number: string | null;
  readonly document_ref: string | null;
  readonly group_id: string;
  readonly group_name: string;
  readonly currency: string;
  /** Minor-unit digits of `currency`, so the console formats exactly. */
  readonly digits: number;
  readonly requested_deposit_minor: string;
  readonly status: 'PENDING' | 'APPROVED' | 'REJECTED';
  readonly requested_by_staff_id: string | null;
  readonly requested_by_login: string;
  readonly requested_at: string;
  readonly decided_by_staff_id: string | null;
  readonly decided_by_login: string | null;
  readonly decided_at: string | null;
  readonly decision_reason: string | null;
  readonly account_id: string | null;
  readonly account_login: string | null;
}

export interface StaffApplicationsResponse {
  readonly rows: readonly StaffApplicationRow[];
  readonly server_time: string;
}

export interface StaffApplicationSubmitResponse {
  readonly ok: true;
  readonly application: StaffApplicationRow;
  readonly audit_id: string;
  readonly server_time: string;
}

export interface StaffApplicationDecisionResponse {
  readonly ok: true;
  readonly application: StaffApplicationRow;
  /** Null on a rejection: a rejected application creates nothing. */
  readonly account: {
    readonly account_id: string;
    readonly login: string;
    readonly currency: string;
    readonly opening_balance_minor: string;
    readonly funding_seq: string | null;
    /** Returned exactly once; the server keeps only the hash. */
    readonly one_time_password: string | null;
  } | null;
  /**
   * P12.9: the expected KYC documents this application did **not** carry when it
   * was decided.
   *
   * Approval is not blocked on this — see docs/19 §15.4 — but the gap is recorded
   * on the decision's audit row and shown here, so an approval without an ID
   * document names the operator who made it. Empty means complete.
   */
  readonly missing_documents: readonly DocumentKindWire[];
  readonly documents_present: number;
  readonly audit_id: string;
  readonly server_time: string;
}



/* ------------------------------------------------------------------ */
/* P12.6 — deposit and withdrawal requests                             */
/* ------------------------------------------------------------------ */

/**
 * Ask for money in or out.
 *
 * The amount is **minor units** on the wire, like every other money field: the
 * console converts the operator's "1,000.00" with the account's own currency
 * digits, and nothing in the platform parses a decimal with a float.
 */
export const PaymentRequestCreate = z.strictObject({
  account_id: z.uuid(),
  kind: z.enum(PAYMENT_KINDS),
  amount_minor: z.string().regex(/^\d{1,15}$/),
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().min(3).max(120),
});

export const PaymentDecisionRequest = z.strictObject({
  request_id: z.uuid(),
  reason: REASON,
});

export const PaymentCancelRequest = z.strictObject({
  request_id: z.uuid(),
});

/** One pending row's account, valued at one mark snapshot (the desk's own numbers). */
export interface StaffPaymentAccountNumbers {
  readonly balance_minor: string;
  readonly equity_minor: string;
  readonly used_margin_minor: string;
  readonly free_margin_minor: string;
  readonly margin_level_bp: string | null;
  readonly margin_call_level_bp: string;
  readonly stop_out_level_bp: string;
  readonly valuable: boolean;
  readonly valuation_issue: string | null;
}

/**
 * What approving this request would do to the account, before it is approved.
 *
 * For a withdrawal this is the decision the operator is making: the money leaves,
 * equity falls by the same amount, and free margin and margin level fall with it.
 * `overdraw` is the one that cannot be approved; `margin_call_after` and
 * `stop_out_after` are the ones the operator must see and may still choose.
 */
export interface StaffPaymentConsequence {
  readonly balance_after_minor: string;
  readonly equity_after_minor: string;
  readonly free_margin_after_minor: string;
  readonly margin_level_after_bp: string | null;
  readonly overdraw: boolean;
  readonly margin_call_after: boolean;
  readonly stop_out_after: boolean;
}

export interface StaffPaymentRow {
  readonly request_id: string;
  readonly account_id: string;
  readonly login: string;
  readonly kind: PaymentKindWire;
  readonly amount_minor: string;
  readonly currency: string;
  readonly digits: number;
  readonly method: PaymentMethodWire;
  readonly reference: string;
  readonly status: PaymentStatusWire;
  readonly requested_by_staff_id: string;
  readonly requested_by_login: string;
  readonly requested_at: string;
  readonly decided_by_staff_id: string | null;
  readonly decided_by_login: string | null;
  readonly decided_at: string | null;
  readonly decision_reason: string | null;
  readonly operation_seq: string | null;
  readonly account: StaffPaymentAccountNumbers | null;
  readonly consequence: StaffPaymentConsequence | null;
  /** False when this caller may not decide it (role, or it is their own request). */
  readonly can_decide: boolean;
  /** True when the caller is the requester: segregation of duties, stated. */
  readonly requested_by_caller: boolean;
}

export interface StaffPaymentsResponse {
  readonly rows: readonly StaffPaymentRow[];
  readonly server_time: string;
}

export interface StaffPaymentResponse {
  readonly ok: true;
  readonly request: StaffPaymentRow;
  readonly server_time: string;
}

export interface StaffPaymentApprovalResponse {
  readonly ok: true;
  readonly request_id: string;
  readonly status: 'APPROVED';
  /** True when the request had already been approved: nothing was written. */
  readonly idempotent: boolean;
  /** The ledger operation; it is the request id, so one request is one movement. */
  readonly operation_id: string;
  readonly journal_seq: string;
  readonly balance_before_minor: string;
  readonly balance_after_minor: string;
  readonly events: readonly unknown[];
  readonly request: StaffPaymentRow;
  readonly server_time: string;
}

/* ------------------------------------------------------------------ */
/* P12.4 — market data: sources, failover, health, repair             */
/* ------------------------------------------------------------------ */

/**
 * A source's availability for one instrument.
 *
 * `HEALTHY`/`STALE`/`DOWN`/`IDLE` are the router's own vocabulary
 * (`services/common/src/feed-router.ts`): a tick inside the source's
 * `staleness_ms`, a silence beyond it, a declared connection failure, or no tick
 * at all since the process started. `DOWN` is the one derived from an adapter's
 * claim; the other three come from the `ticks` table.
 */
export type AdminFeedState = 'HEALTHY' | 'STALE' | 'DOWN' | 'IDLE';

export interface AdminFeedSourceSymbolRow {
  /** The provider's symbol (`XAU_USD`). */
  readonly source_symbol: string;
  /** The platform instrument it prices (`XAUUSD`). */
  readonly symbol_name: string;
  /** Availability of this source for this symbol, from the tick evidence. */
  readonly state: AdminFeedState;
  readonly last_tick_at: string | null;
  readonly age_ms: number | null;
  /** Ticks in the last 60 s — the panel's "ticks/s", in the unit an operator reads. */
  readonly ticks_60s: number;
  /** The largest inter-tick gap in the last hour, or null with fewer than two ticks. */
  readonly max_gap_ms: number | null;
}

export interface AdminFeedSourceRow {
  readonly source_id: string;
  /** The wire identity and `ticks.source`. */
  readonly name: string;
  readonly kind: 'SIMULATED' | 'WEBSOCKET_JSON';
  readonly priority: number;
  readonly enabled: boolean;
  readonly staleness_ms: number;
  readonly config: unknown;
  readonly symbols: readonly AdminFeedSourceSymbolRow[];
  /** What the adapter itself last declared, or null. A claim, shown as one. */
  readonly declared: {
    readonly state: 'UP' | 'DOWN';
    readonly at: string;
    readonly detail: string;
  } | null;
  /** Symbols of this tenant this source is currently serving. */
  readonly serving: readonly string[];
  readonly transitions_last_hour: number;
}

export interface AdminFeedSymbolRow {
  readonly symbol_id: string;
  readonly symbol: string;
  /** Null means **unserved**: no enabled source has a healthy quote for it. */
  readonly serving_source: string | null;
  /** The best available source, or null when nothing can serve the instrument. */
  readonly state: AdminFeedState | 'UNSERVED';
  readonly age_ms: number | null;
  readonly last_tick_at: string | null;
}

export interface AdminFeedTransitionRow {
  readonly transition_id: string;
  readonly symbol: string;
  readonly from_source: string | null;
  readonly to_source: string | null;
  readonly from_bid_e8: string | null;
  readonly from_ask_e8: string | null;
  readonly to_bid_e8: string | null;
  readonly to_ask_e8: string | null;
  /** `FIRST` | `FAILOVER` | `RECOVERY` | `UNSERVED` | `CONFIG`. */
  readonly reason: string;
  readonly observed_at: string;
}

export interface AdminFeedRepairRow {
  readonly repair_id: string;
  readonly symbol: string;
  readonly from_ts: string;
  readonly to_ts: string;
  readonly action: 'REPAIRED' | 'REFUSED';
  readonly reason: string;
  readonly source_name: string | null;
  readonly missing_bars: number;
  readonly bars_written: number;
  readonly ticks_written: number;
  readonly requested_by: string;
  readonly at: string;
}

export interface AdminFeedResponse {
  readonly sources: readonly AdminFeedSourceRow[];
  readonly symbols: readonly AdminFeedSymbolRow[];
  readonly transitions: readonly AdminFeedTransitionRow[];
  readonly repairs: readonly AdminFeedRepairRow[];
  /** P12.5: cross-source guard trips, newest first, with both prices. */
  readonly guards: readonly AdminFeedGuardRow[];
  readonly server_time: string;
}

/**
 * One cross-source guard trip (P12.5), as the desk reads it.
 *
 * Both prices and both sources are the evidence: a reader can recompute the
 * deviation and decide whether the guard was right. `active` is the cooldown's
 * verdict at the instant the panel was built, so "entries are refused right now"
 * is one boolean rather than arithmetic in the browser.
 */
export interface AdminFeedGuardRow {
  readonly event_id: string;
  readonly symbol: string;
  readonly from_source: string;
  readonly to_source: string;
  readonly from_bid_e8: string;
  readonly from_ask_e8: string;
  readonly to_bid_e8: string;
  readonly to_ask_e8: string;
  readonly deviation_e8: string;
  readonly threshold_e8: string;
  readonly observed_at: string;
  readonly cooldown_until: string;
  readonly active: boolean;
}

export interface AdminFeedSourceSaveResponse {
  readonly ok: true;
  readonly source_id: string;
  readonly server_time: string;
}

export interface AdminFeedRepairResponse {
  readonly ok: true;
  readonly action: 'REPAIRED' | 'REFUSED';
  readonly reason: string;
  readonly source_name: string | null;
  readonly missing_minutes: number;
  readonly bars_written: number;
  readonly ticks_written: number;
  readonly detail: string;
  readonly server_time: string;
}


/* ------------------------------------------------------------------ */
/* P12.9 — KYC documents                                              */
/* ------------------------------------------------------------------ */

/**
 * The document kinds a small broker files against an application.
 *
 * A closed set, because "proof of address" has to be a queryable fact for the
 * approval to be able to say which document is missing. `OTHER` exists so a broker
 * can file something the platform has not modelled without inventing a kind.
 */
export const DOCUMENT_KINDS = [
  'ID_DOCUMENT',
  'PROOF_OF_ADDRESS',
  'PROOF_OF_FUNDS',
  'OTHER',
] as const;
export type DocumentKindWire = (typeof DOCUMENT_KINDS)[number];

/**
 * The formats the platform is willing to store, by **sniffed** type.
 *
 * The client-declared `content_type` is only a hint: the service reads the magic
 * bytes and refuses anything else. The list is short on purpose — every format
 * here is one the platform can serve back with a type it has proven, and none of
 * them can execute anything in a browser.
 */
export const DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export type DocumentContentTypeWire = (typeof DOCUMENT_CONTENT_TYPES)[number];

/** 5 MiB. The service refuses above it; the table states the same number. */
export const DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The stored extension for a sniffed type.
 *
 * One definition, used by the uploader (which names the object key), the export
 * (which names the file), the verifier (which checks the name matches the type)
 * and the importer. A second mapping would be a way for the archive and the store
 * to disagree about a document's format.
 *
 * The extension comes from the **bytes**, never from the filename the client sent:
 * a JPEG named `passport.pdf` is stored as `.jpg`.
 */
export function documentExtension(contentType: DocumentContentTypeWire): 'pdf' | 'png' | 'jpg' {
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType === 'image/png') return 'png';
  return 'jpg';
}

/**
 * What "the expected documents are present" means, in one place.
 *
 * An `INDIVIDUAL` application is expected to carry one identity document and one
 * proof of address: those are the two a small broker's KYC file is built around.
 * A `SYSTEM` application has no client behind it (provisioning, the operator CLI),
 * so nothing is expected — and pretending otherwise would make every provisioned
 * account permanently "incomplete".
 *
 * This is the definition the server uses to warn the decider and to record the gap
 * on the decision, and the console uses to show it; there is no second copy.
 */
export function expectedDocumentKinds(
  applicationKind: 'INDIVIDUAL' | 'SYSTEM',
): readonly DocumentKindWire[] {
  return applicationKind === 'SYSTEM' ? [] : ['ID_DOCUMENT', 'PROOF_OF_ADDRESS'];
}

/** The expected kinds that are not present, in the expected order. */
export function missingDocumentKinds(
  applicationKind: 'INDIVIDUAL' | 'SYSTEM',
  present: readonly DocumentKindWire[],
): DocumentKindWire[] {
  const have = new Set(present);
  return expectedDocumentKinds(applicationKind).filter((kind) => !have.has(kind));
}

/**
 * Upload one document. The bytes are base64 in a JSON envelope: there is no
 * pre-signed URL and no direct PUT to the object store, because a delivery
 * decision must not live in the client. The service sniffs the bytes, stores them
 * and answers with the metadata it recorded.
 */
export const ApplicationDocumentUploadRequest = z.strictObject({
  application_id: z.uuid(),
  kind: z.enum(DOCUMENT_KINDS),
  filename: z.string().min(1).max(200),
  /** What the client thinks it is sending. The service refuses a mismatch. */
  content_type: z.enum(DOCUMENT_CONTENT_TYPES),
  /** Base64 of the file. 5 MiB is ~7 MB encoded; the route's body cap is 8 MiB. */
  content_base64: z.string().min(1).max(8_000_000),
});

export interface StaffApplicationDocument {
  readonly document_id: string;
  readonly application_id: string;
  readonly kind: DocumentKindWire;
  readonly original_filename: string;
  /** The sniffed type — what the bytes are, not what the client said. */
  readonly content_type: DocumentContentTypeWire;
  readonly byte_size: string;
  readonly sha256: string;
  readonly uploaded_by_staff_id: string | null;
  readonly uploaded_by_login: string;
  readonly uploaded_at: string;
}

export interface StaffApplicationDocumentsResponse {
  readonly application_id: string;
  readonly documents: readonly StaffApplicationDocument[];
  /** Expected kinds this application does not carry yet. Empty = complete. */
  readonly missing: readonly DocumentKindWire[];
  readonly expected: readonly DocumentKindWire[];
  readonly server_time: string;
}

export interface StaffApplicationDocumentUploadResponse {
  readonly ok: true;
  readonly document: StaffApplicationDocument;
  readonly missing: readonly DocumentKindWire[];
  readonly server_time: string;
}

/** One row of the download audit trail. */
export interface StaffDocumentAccessRow {
  readonly access_id: string;
  readonly document_id: string;
  readonly staff_id: string;
  readonly staff_login: string;
  readonly staff_role: string;
  readonly action: 'DOWNLOAD';
  readonly byte_size: string;
  readonly sha256: string;
  readonly at: string;
}

export interface StaffDocumentAccessResponse {
  readonly application_id: string;
  readonly document_id: string;
  readonly rows: readonly StaffDocumentAccessRow[];
  readonly server_time: string;
}